import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import AgentStatusPanel from '../../components/navigation/AgentStatusPanel';
import DashboardStats from './components/DashboardStats';
import FilterControls from './components/FilterControls';
import ProjectsTable from './components/ProjectsTable';
import ProjectsGrid from './components/ProjectsGrid';
import BulkActionsBar from './components/BulkActionsBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const MainDashboard = () => {
  const navigate = useNavigate();
  const [agentPanelOpen, setAgentPanelOpen] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [loading, setLoading] = useState(true);
  const [selectedProjects, setSelectedProjects] = useState([]);

  const [filters, setFilters] = useState({
    search: '',
    country: 'all',
    projectType: 'all',
    status: 'all',
    standard: 'all'
  });

  const [projects, setProjects] = useState([
    {
      id: 'PRJ-2026-001',
      name: 'Restauración Forestal Amazonía',
      country: 'Colombia',
      projectType: 'A/R',
      standard: 'Verra',
      status: 'Validado',
      tco2ePerYear: 125000,
      erPerHa: 8.5
    },
    {
      id: 'PRJ-2026-002',
      name: 'Conservación Bosque Nublado',
      country: 'Perú',
      projectType: 'REDD+',
      standard: 'Gold Standard',
      status: 'En Verificación',
      tco2ePerYear: 98000,
      erPerHa: 12.3
    },
    {
      id: 'PRJ-2026-003',
      name: 'Manejo Forestal Sostenible Chocó',
      country: 'Colombia',
      projectType: 'IFM',
      standard: 'Verra',
      status: 'En Desarrollo',
      tco2ePerYear: 76000,
      erPerHa: 6.8
    },
    {
      id: 'PRJ-2026-004',
      name: 'Ganadería Regenerativa Pampa',
      country: 'Argentina',
      projectType: 'Ganadería Regenerativa',
      standard: 'Gold Standard',
      status: 'Validado',
      tco2ePerYear: 54000,
      erPerHa: 4.2
    },
    {
      id: 'PRJ-2026-005',
      name: 'Carbono Orgánico Suelos Agrícolas',
      country: 'Brasil',
      projectType: 'SOC',
      standard: 'Verra',
      status: 'En Verificación',
      tco2ePerYear: 89000,
      erPerHa: 5.6
    },
    {
      id: 'PRJ-2026-006',
      name: 'Reforestación Andes Ecuatorianos',
      country: 'Ecuador',
      projectType: 'A/R',
      standard: 'Gold Standard',
      status: 'Pendiente',
      tco2ePerYear: 67000,
      erPerHa: 7.9
    },
    {
      id: 'PRJ-2026-007',
      name: 'Protección Bosque Seco Tropical',
      country: 'Bolivia',
      projectType: 'REDD+',
      standard: 'Verra',
      status: 'En Desarrollo',
      tco2ePerYear: 112000,
      erPerHa: 10.4
    },
    {
      id: 'PRJ-2026-008',
      name: 'Manejo Forestal Patagonia',
      country: 'Chile',
      projectType: 'IFM',
      standard: 'Gold Standard',
      status: 'Validado',
      tco2ePerYear: 43000,
      erPerHa: 5.1
    }
  ]);

  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    totalTco2e: 0,
    activeAgents: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 800));

      const totalTco2e = projects?.reduce((sum, p) => sum + p?.tco2ePerYear, 0);
      const activeProjects = projects?.filter(
        (p) => p?.status === 'Validado' || p?.status === 'En Verificación'
      )?.length;

      setStats({
        totalProjects: projects?.length,
        activeProjects,
        totalTco2e,
        activeAgents: 5
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = React.useMemo(() => {
    return projects?.filter((project) => {
      const matchesSearch =
        filters?.search === '' ||
        project?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        project?.id?.toLowerCase()?.includes(filters?.search?.toLowerCase());

      const matchesCountry = filters?.country === 'all' || project?.country === filters?.country;
      const matchesType = filters?.projectType === 'all' || project?.projectType === filters?.projectType;
      const matchesStatus = filters?.status === 'all' || project?.status === filters?.status;
      const matchesStandard = filters?.standard === 'all' || project?.standard === filters?.standard;

      return matchesSearch && matchesCountry && matchesType && matchesStatus && matchesStandard;
    });
  }, [projects, filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      country: 'all',
      projectType: 'all',
      status: 'all',
      standard: 'all'
    });
  };

  const handleProjectClick = (project) => {
    navigate(`/project-detail-view?id=${project?.id}`);
  };

  const handleStatusUpdate = (project) => {
    console.log('Update status for project:', project?.id);
  };

  const handleCreateProject = () => {
    console.log('Create new project');
  };

  const handleBulkExport = () => {
    console.log('Export selected projects:', selectedProjects);
  };

  const handleBulkDelete = () => {
    console.log('Delete selected projects:', selectedProjects);
  };

  const handleBulkUpdateStatus = () => {
    console.log('Update status for selected projects:', selectedProjects);
  };

  const handleClearSelection = () => {
    setSelectedProjects([]);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex">
        <main className="flex-1 pt-16">
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8">
              <div className="flex items-center justify-between mb-2">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
                  Panel de Control
                </h1>
                <div className="flex items-center gap-2">
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('table')}
                    iconName="Table"
                    iconSize={18}
                    title="Vista de tabla"
                    className="hidden md:flex"
                  />
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('grid')}
                    iconName="LayoutGrid"
                    iconSize={18}
                    title="Vista de cuadrícula"
                    className="hidden md:flex"
                  />
                </div>
              </div>
              <p className="text-sm md:text-base text-muted-foreground">
                Monitoreo de proyectos AFOLU y coordinación de agentes especializados
              </p>
            </div>

            {loading ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                  {[...Array(4)]?.map((_, i) => (
                    <div key={i} className="skeleton h-24 rounded-md" />
                  ))}
                </div>
                <div className="skeleton h-64 rounded-md" />
              </div>
            ) : (
              <>
                <DashboardStats stats={stats} />

                <div className="mt-6 md:mt-8">
                  <FilterControls
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onReset={handleResetFilters}
                    onCreateProject={handleCreateProject}
                  />
                </div>

                <div className="mt-6 md:mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg md:text-xl font-semibold text-foreground font-mono">
                      Proyectos de Carbono
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground caption">
                      <Icon name="Filter" size={16} />
                      <span>
                        {filteredProjects?.length} de {projects?.length} proyectos
                      </span>
                    </div>
                  </div>

                  {filteredProjects?.length === 0 ? (
                    <div className="card text-center py-12">
                      <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-muted rounded-full">
                        <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        No se encontraron proyectos
                      </h3>
                      <p className="text-sm text-muted-foreground caption mb-4">
                        Intenta ajustar los filtros o crear un nuevo proyecto
                      </p>
                      <Button variant="default" onClick={handleCreateProject} iconName="Plus" iconPosition="left">
                        Crear Proyecto
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="hidden lg:block">
                        <ProjectsTable
                          projects={filteredProjects}
                          onProjectClick={handleProjectClick}
                          onStatusUpdate={handleStatusUpdate}
                        />
                      </div>
                      <div className="lg:hidden">
                        <ProjectsGrid
                          projects={filteredProjects}
                          onProjectClick={handleProjectClick}
                          onStatusUpdate={handleStatusUpdate}
                        />
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </main>

        <AgentStatusPanel isOpen={agentPanelOpen} onToggle={setAgentPanelOpen} />
      </div>
      <BulkActionsBar
        selectedCount={selectedProjects?.length}
        onExport={handleBulkExport}
        onDelete={handleBulkDelete}
        onUpdateStatus={handleBulkUpdateStatus}
        onClearSelection={handleClearSelection}
      />
    </div>
  );
};

export default MainDashboard;